import { Options } from 'ky';
import { getGearDateFilterFormat, getGearDateTimeFilterFormat } from '@cs-cms/utils';
import {
  CommitUploadSessionRequest,
  CreateSmallDocumentWithMetadataRequest,
  DEFAULT_UPLOAD_TIMEOUT,
  DeleteDocumentsRequest,
  DeleteDocumentsResponse,
  DownloadManyDocumentsRequest,
  CpsDocumentMetadata,
  GetDocumentMetadataRequest,
  LockUnlockDocumentsInfoRequest,
  LockUnlockResponse,
  NewUploadSessionResponse,
  ResponseWithHeaders,
  StartNewDocumentUploadSessionRequest,
  StartUploadSessionRequest,
  UpdateDocumentMetadata,
  UpdateSmallDocumentWithMetadataRequest,
  UploadChunkToSessionRequest,
  ValidateMetadataRequest
} from '@cs-cms/types';
import { ExecuteEndpoint, ExecuteEndpointWithHeaders, ExecuteFileDownload } from '../base/BaseApiService';

type SerializationValueUnionType = string | number | boolean;

export const buildDocumentEndpointPath = (
  contentType: string,
  documentId: string,
  indexId?: number,
  displayInBrowser = false
) =>
  `content-types/${contentType}/documents/${documentId}/content?indexId=${indexId}&displayInBrowser=${displayInBrowser}`;

export class DocumentsService {
  public async lockDocuments(dto: LockUnlockDocumentsInfoRequest): Promise<LockUnlockResponse> {
    return ExecuteEndpoint(`indexes/${dto.indexId}/documents/lock`, 'POST', { json: dto });
  }

  public async unlockDocuments(dto: LockUnlockDocumentsInfoRequest): Promise<LockUnlockResponse> {
    return ExecuteEndpoint(`indexes/${dto.indexId}/documents/unlock`, 'POST', { json: dto });
  }

  public async deleteDocuments(dto: DeleteDocumentsRequest): Promise<DeleteDocumentsResponse> {
    return ExecuteEndpoint(`indexes/${dto.indexId}/documents/delete`, 'POST', { json: dto });
  }

  public async updateSmallDocumentWithMetadata(
    dto: UpdateSmallDocumentWithMetadataRequest,
    etag?: string
  ): Promise<{ blobName: string }> {
    const formData = new FormData();

    if (dto.file) {
      formData.append('File', dto.file);
    }

    const processedMetadata = this.processMetadataForSerialization(dto.metadata);

    formData.append('Metadata', JSON.stringify({ Metadata: processedMetadata }));

    const options: Options = {
      body: formData,
      ...(etag && {
        headers: {
          'If-Match': etag
        }
      })
    };

    return ExecuteEndpoint(
      `content-types/${dto.ctName}/documents/${dto.documentId}/content?indexId=${dto.indexId}`,
      'PUT',
      options,
      DEFAULT_UPLOAD_TIMEOUT
    );
  }

  public async createSmallDocumentWithMetadata(dto: CreateSmallDocumentWithMetadataRequest): Promise<string> {
    const formData = new FormData();
    formData.append('File', dto.file);
    const processedMetadata = this.processMetadataForSerialization(dto.metadata);

    formData.append('Metadata', JSON.stringify({ Metadata: processedMetadata }));

    return ExecuteEndpoint(
      `content-types/${dto.ctName}/documents?indexId=${dto.indexId}`,
      'POST',
      { body: formData },
      DEFAULT_UPLOAD_TIMEOUT
    );
  }

  public async updateDocumentMetadata(
    dto: UpdateDocumentMetadata,
    etag?: string
  ): Promise<{ metadata: CpsDocumentMetadata[] }> {
    const options: Options = {
      json: dto,
      ...(etag && {
        headers: {
          'If-Match': etag
        }
      })
    };
    return ExecuteEndpoint(`content-types/${dto.ctName}/documents/${dto.documentId}`, 'POST', options);
  }

  public async getDocumentMetadata(
    dto: GetDocumentMetadataRequest
  ): Promise<ResponseWithHeaders<{ metadata: CpsDocumentMetadata }>> {
    const searchParams = new URLSearchParams();
    searchParams.append('indexId', dto.indexId.toString());
    const queryParams = `?${searchParams.toString()}`;
    return ExecuteEndpointWithHeaders(`content-types/${dto.ctName}/documents/${dto.documentId}${queryParams}`, 'GET');
  }

  public async validateDocumentMetadata(dto: ValidateMetadataRequest): Promise<string[]> {
    const { ctName, ...bodyData } = dto;
    return ExecuteEndpoint(`content-types/${ctName}/metadataValidation`, 'POST', { json: bodyData });
  }

  public async downloadManyDocuments(dto: DownloadManyDocumentsRequest, zipName: string): Promise<void> {
    return ExecuteFileDownload(
      'documents/download-many',
      'POST',
      {
        json: dto,
        headers: {
          Accept: 'application/octet-stream'
        }
      },
      zipName
    );
  }

  public async startUploadSession(dto: StartUploadSessionRequest): Promise<NewUploadSessionResponse> {
    const formData = new FormData();
    formData.append('File', dto.chunk.file);
    formData.append('ChunkIndex', '0');

    const endpoint = `content-types/${dto.ctName}/documents/${dto.documentId}/sessions?indexId=${dto.indexId}`;

    return ExecuteEndpoint(endpoint, 'PUT', { body: formData }, DEFAULT_UPLOAD_TIMEOUT);
  }

  public async uploadChunk(dto: UploadChunkToSessionRequest): Promise<void> {
    const formData = new FormData();
    formData.append('File', dto.chunk.file);
    formData.append('ChunkIndex', dto.chunk.chunkIndex.toString());

    return ExecuteEndpoint(
      `content-types/${dto.ctName}/documents/sessions/${dto.sessionId}?indexId=${dto.indexId}`,
      'PUT',
      { body: formData },
      DEFAULT_UPLOAD_TIMEOUT
    );
  }

  public async startNewDocumentUploadSession(
    dto: StartNewDocumentUploadSessionRequest
  ): Promise<NewUploadSessionResponse> {
    const formData = new FormData();

    formData.append('File', dto.chunk.file);
    formData.append('ChunkIndex', '0');

    return ExecuteEndpoint(
      `content-types/${dto.ctName}/documents/sessions?searchIndexId=${dto.searchIndexId}`,
      'PUT',
      { body: formData },
      DEFAULT_UPLOAD_TIMEOUT
    );
  }

  public async commitUploadSession(dto: CommitUploadSessionRequest): Promise<string> {
    return ExecuteEndpoint(
      `content-types/${dto.ctName}/documents/sessions/${dto.sessionId}`,
      'POST',
      { json: dto },
      DEFAULT_UPLOAD_TIMEOUT
    );
  }

  private processMetadataForSerialization(
    metadata: CpsDocumentMetadata
  ): Record<string, SerializationValueUnionType | undefined> {
    const processedMetadata: Record<string, SerializationValueUnionType | undefined> = {};

    for (const [key, value] of Object.entries(metadata)) {
      if (value instanceof Date) {
        const isDateOnly = value.getHours() === 0 && value.getMinutes() === 0 && value.getSeconds() === 0;

        if (isDateOnly) {
          processedMetadata[key] = getGearDateFilterFormat(value);
        } else {
          processedMetadata[key] = getGearDateTimeFilterFormat(value);
        }
      } else {
        processedMetadata[key] = value;
      }
    }

    return processedMetadata;
  }
}
